import { z } from 'zod';

import { Option } from '../../types/Option';

export const wireSizes = [
    '1',
    '1.5',
    '2.5',
    '4',
    '6',
    '10',
    '16',
    '25',
    '26.67',
    '35',
    '0.05',
    '0.08',
    '0.14',
    '0.25',
    '0.34',
    '0.38',
    '0.5',
    '0.75',
    '50',
    '55',
    '70',
    '95',
    '120',
    '127',
    '150',
    '185',
    '240',
    '300',
    '355',
    '400',
    '407',
    '453',
    '500',
    '635',
    '762',
    '889',
    '1016',
] as const;

const wireSizesAWG = [
    '16',
    '14',
    '12',
    '10',
    '8',
    '6',
    '3',
    '4',
    '2',
    '30',
    '28',
    '26',
    '24',
    '22',
    '21',
    '20',
    '18',
    '17',
    '1',
    '1/0',
    '2/0',
    '3/0',
    '4/0',
    '250MCM',
    '300MCM',
    '350MCM',
    '500MCM',
    '600MCM',
    '700MCM',
    '750MCM',
    '800MCM',
    '900MCM',
    '1000MCM',
    '1250MCM',
    '1500MCM',
    '1750MCM',
    '2000MCM',
] as const;

export const IECWireSizes = [
    '1.5',
    '2.5',
    '4',
    '6',
    '10',
    '16',
    '25',
    '35',
    '50',
    '70',
    '95',
    '120',
    '150',
    '185',
    '240',
    '300',
    '355',
    '400',
    '407',
    '453',
    '500',
    '635',
    '762',
    '889',
    '1016',
] as const;

export type WireSize = (typeof wireSizes)[number];
export type WireSizeAWG = (typeof wireSizesAWG)[number];
export type WireSizeIEC = (typeof IECWireSizes)[number];

const map: {
    [key in WireSize]: WireSizeAWG;
} = {
    '0.05': '30',
    '0.08': '28',
    '0.14': '26',
    '0.25': '24',
    '0.34': '22',
    '0.38': '21',
    '0.5': '20',
    '0.75': '18',
    '1': '17',
    '1.5': '16',
    '2.5': '14',
    '4': '12',
    '6': '10',
    '10': '8',
    '16': '6',
    '25': '4',
    '26.67': '3',
    '35': '2',
    '50': '1',
    '55': '1/0',
    '70': '2/0',
    '95': '3/0',
    '120': '4/0',
    '127': '250MCM',
    '150': '300MCM',
    '185': '350MCM',
    '240': '500MCM',
    '300': '600MCM',
    '355': '700MCM',
    '400': '750MCM',
    '407': '800MCM',
    '453': '900MCM',
    '500': '1000MCM',
    '635': '1250MCM',
    '762': '1500MCM',
    '889': '1750MCM',
    '1016': '2000MCM',
};

const toAWG = (value: WireSize): WireSizeAWG => {
    return map[value];
};

const toMM2 = (value: WireSizeAWG): WireSize => {
    return Object.entries(map).find(([_, val]) => val === value)?.[0] as WireSize;
};

const options = wireSizes.map((option) => ({
    value: option,
    label: `${option} mm2, ${toAWG(option)} AWG`,
    mm2: option,
    awg: toAWG(option),
})) as Option<WireSize>[];

const validator = z.preprocess((value) => {
    if (!value) {
        return null;
    }
    const parsed = parseFloat(String(value));
    return isNaN(parsed) ? null : String(parsed);
}, z.enum(wireSizes).nullable().default(null));

export const WireSize = {
    validator,
    toAWG,
    toMM2,
    options,
};
